import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../../utils/config';
import { getCategoryName } from '../../../../utils/categories';
import UserLayout from '../../layout/UserLayout';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import SuccessModal from '../../../../components/SuccessModal';
import './CartPage.scss';

interface CartItem {
  product_id?: string;
  product_name: string;
  product_brand?: string;
  quantity: number;
  added_at: string;
  is_manual: boolean;
  // Manual product fields
  company_product_code?: string;
  dimensions?: string;
  base_unit?: string;
  color?: string;
  category?: string;
  description?: string;
  budget?: number;
  currency?: string;
  // Image
  image_url?: string;
  // Full product details (fetched from backend)
  fullProductDetails?: {
    product_id: string;
    name: string;
    brand: string;
    color?: string;
    dimensions?: string;
    amazon_asin?: string;
    category?: string[];
    description?: string;
    base_unit?: string;
    variant_id?: string;
  };
}

interface Prerequest {
  _id: string;
  user_id: string;
  company_id: string;
  items: CartItem[];
  created_at: string;
  updated_at: string;
}

const CartPage: React.FC = () => {
  const { user } = useAuth();
  const [cartData, setCartData] = useState<Prerequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [productImages, setProductImages] = useState<{ [key: string]: string }>({});
  const [productDetails, setProductDetails] = useState<{ [key: string]: any }>({});
  const [editingQuantities, setEditingQuantities] = useState<{ [key: number]: string }>({});

  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchCartData();
  }, []);

  const fetchCartData = async () => {
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user?._id || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCartData(data.data);

        // Fetch product images and details for marketplace items
        if (data.data && data.data.items) {
          await fetchProductImages(data.data.items);
          await fetchProductDetails(data.data.items);
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to fetch cart data:', errorData);
      }
    } catch (error) {
      console.error('❌ Error fetching cart data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemIndex: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(itemIndex);
      return;
    }

    setUpdating(itemIndex.toString());
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/prerequests/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user?._id || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_index: itemIndex,
          quantity: newQuantity
        })
      });

      if (response.ok) {
        await fetchCartData(); // Refresh cart
      } else {
        console.error('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setUpdating(null);
    }
  };

  const removeItem = async (itemIndex: number) => {
    setUpdating(itemIndex.toString());
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/prerequests/remove`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user?._id || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_index: itemIndex
        })
      });

      if (response.ok) {
        await fetchCartData(); // Refresh cart
      } else {
        console.error('Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      setUpdating(null);
    }
  };

  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<string>('');
  const [selectedInvoiceAddress, setSelectedInvoiceAddress] = useState<string>('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch addresses on mount
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user?.company_id) return;

      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/addresses/companies/${user.company_id}/addresses`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAddresses(data.data || []);

          // Set default addresses if available
          const defaultAddress = data.data?.find((addr: any) => addr.is_default);
          if (defaultAddress) {
            setSelectedDeliveryAddress(defaultAddress._id);
            setSelectedInvoiceAddress(defaultAddress._id);
          }
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
      }
    };

    // Set user info
    setContactPerson(user?.name || '');
    setContactEmail(user?.email || '');

    fetchAddresses();
  }, [user]);

  const handleSubmitRequest = () => {
    if (!selectedDeliveryAddress || !selectedInvoiceAddress) {
      alert('Please select delivery and invoice addresses');
      return;
    }

    if (!contactPerson || !contactEmail) {
      alert('Please fill in contact information');
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const confirmSubmitRequest = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');

      // Get full address objects
      const deliveryAddr = addresses.find(a => a._id === selectedDeliveryAddress);
      const invoiceAddr = addresses.find(a => a._id === selectedInvoiceAddress);

      // Format addresses as strings
      const deliveryAddress = `${deliveryAddr?.street || ''}, ${deliveryAddr?.city || ''}, ${deliveryAddr?.state || ''} ${deliveryAddr?.postal_code || ''}, ${deliveryAddr?.country || ''}`;
      const invoiceAddress = `${invoiceAddr?.street || ''}, ${invoiceAddr?.city || ''}, ${invoiceAddr?.state || ''} ${invoiceAddr?.postal_code || ''}, ${invoiceAddr?.country || ''}`;

      // Generate title from product names
      const productNames = cartData?.items?.map(item => item.product_name).filter(Boolean) || [];
      const title = productNames.length > 0
        ? productNames.join(', ')
        : `Request - ${new Date().toLocaleDateString('en-US')}`;

      const requestPayload = {
        title: title,
        delivery_address: deliveryAddress,
        invoice_address: invoiceAddress,
        contact_person: contactPerson,
        contact_email: contactEmail
      };

      const response = await fetch(API_ENDPOINTS.REQUESTS_V2, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user?._id || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (response.ok) {
        const data = await response.json();
        const requestId = data.data._id;
        console.log('Request created successfully:', data);

        // Add products to request
        for (let i = 0; i < (cartData?.items?.length || 0); i++) {
          const item = cartData!.items[i];

          // Get full product details
          const fullProduct = getProductDetails(item);

          const productPayload = {
            product_id: item.product_id,
            product_name: item.product_name,
            product_brand: fullProduct?.brand || item.product_brand || '',
            product_global_code: fullProduct?.product_global_code || '',
            amazon_asin: fullProduct?.amazon_asin || '',
            dimensions: fullProduct?.dimensions || item.dimensions || '',
            base_unit: fullProduct?.base_unit || item.base_unit || 'unit',
            color: fullProduct?.color || item.color || '',
            category: fullProduct?.category || item.category || '',
            description: fullProduct?.description || item.description || '',
            quantity: item.quantity,
            unit: fullProduct?.base_unit || item.base_unit || 'unit',
            budget: item.budget || 0,
            currency: item.currency || 'TRY',
            notes: '',
            line_number: i + 1,
            is_manual: !!item.is_manual
          };

          console.log(`Adding product ${i + 1}/${cartData!.items.length}:`, productPayload);

          const productResponse = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-User-ID': user?._id || ''
            },
            body: JSON.stringify(productPayload)
          });

          if (!productResponse.ok) {
            const errorData = await productResponse.json();
            console.error(`Failed to add product ${i + 1}:`, errorData);
            throw new Error(`Failed to add product: ${item.product_name}`);
          }

          const productResult = await productResponse.json();
          console.log(`Product ${i + 1} added successfully:`, productResult);
        }

        const msg = `Request created successfully! ${cartData!.items.length} ${cartData!.items.length === 1 ? 'product' : 'products'} added.`;
        setSuccessMessage(msg);

        // Clear cart after successful request creation
        await handleClearCart(true);

        // Show success modal
        setIsSuccessModalOpen(true);

      } else {
        const errorData = await response.json();
        alert(`Failed to create request: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating request:', error);
      alert('An error occurred while creating the request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessModalOpen(false);
    // Navigate to open requests page
    navigate('/dashboard/user/inbox/open');
  };

  const handleClearCart = async (skipConfirmation = false) => {
    if (!skipConfirmation && !window.confirm('Are you sure you want to clear the cart?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(API_ENDPOINTS.PREREQUEST_CLEAR, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user?._id || '',
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        await fetchCartData(); // Refresh cart
      } else {
        console.error('Failed to clear cart');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const fetchProductImages = async (items: CartItem[]) => {
    const token = localStorage.getItem('auth_token');
    const images: { [key: string]: string } = {};

    for (const item of items) {
      if (item.product_id && !item.is_manual) {
        try {
          // Product ID is already ObjectId format, use it directly
          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/products/${item.product_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-User-ID': user?._id || ''
            }
          });

          if (response.ok) {
            const productData = await response.json();
            if (productData.success && productData.data.image_gridfs_id) {
              images[item.product_id] = `${API_ENDPOINTS.BASE_URL.replace('/api', '')}/api/gridfs/${productData.data.image_gridfs_id}`;
            } else {
              images[item.product_id] = '/placeholder-product.png';
            }
          } else {
            // Set placeholder for missing products
            images[item.product_id] = '/placeholder-product.png';
          }
        } catch (error) {
          console.error('Error fetching product image:', error);
          images[item.product_id] = '/placeholder-product.png';
        }
      }
    }

    setProductImages(images);
  };

  const fetchProductDetails = async (items: CartItem[]) => {
    const token = localStorage.getItem('auth_token');
    const details: { [key: string]: any } = {};

    for (const item of items) {
      if (item.product_id && !item.is_manual) {
        try {
          // Product ID is already ObjectId format, use it directly
          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/products/${item.product_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-User-ID': user?._id || ''
            }
          });

          if (response.ok) {
            const productData = await response.json();
            if (productData.success && productData.data) {
              details[item.product_id] = {
                product_id: productData.data.product_id,
                name: productData.data.name,
                brand: productData.data.brand,
                color: productData.data.color,
                dimensions: productData.data.dimensions,
                amazon_asin: productData.data.amazon_asin,
                category: productData.data.category,
                description: productData.data.description,
                base_unit: productData.data.base_unit,
                variant_id: productData.data.variant_id
              };
            }
          }
        } catch (error) {
          console.error('Error fetching product details:', error);
        }
      }
    }

    setProductDetails(details);
  };

  const getProductImage = (item: CartItem) => {
    if (item.product_id && !item.is_manual && productImages[item.product_id]) {
      return productImages[item.product_id];
    }
    return '/images/product-placeholder.png';
  };

  const getProductDetails = (item: CartItem) => {
    if (item.product_id && !item.is_manual && productDetails[item.product_id]) {
      return productDetails[item.product_id];
    }
    return null;
  };

  const getCartContent = () => {
    if (loading) {
      return (
        <div className="cart-page">
          <div className="cart-loading">
            <p>Loading cart...</p>
          </div>
        </div>
      );
    }

    if (!cartData || !cartData.items || cartData.items.length === 0) {
      return (
        <div className="cart-page">
          <div className="cart-empty">
            <div className="empty-cart-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Add products to your cart to start creating a request.</p>
            <button
              className="start-shopping-btn"
              onClick={() => navigate('/dashboard/user/create-request/marketplace')}
            >
              Start Creating Request
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="cart-page">
        <div className="cart-content">
          <div className="cart-items-section">
            <div className="cart-items-container">
              {cartData.items.map((item, index) => (
                <div key={`${item.product_id || item.product_name}_${index}`} className="cart-item">
                  <div className="item-image">
                    <img
                      src={getProductImage(item)}
                      alt=""
                      style={{ display: 'block' }} // Ensure it's visible initially
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.style.backgroundColor = '#f8f9fa'; // Optional: add background to parent
                      }}
                    />
                  </div>

                  <div className="item-details">
                    <div className="item-info">
                      <h3 className="item-name">{item.product_name}</h3>
                      {item.product_brand && (
                        <p className="item-brand">Brand: {item.product_brand}</p>
                      )}
                      {item.is_manual ? (
                        <div className="manual-item-details">
                          {item.company_product_code && (
                            <p className="item-code">Product Code: {item.company_product_code}</p>
                          )}
                          {item.dimensions && (
                            <p className="item-dimensions">Dimensions: {item.dimensions}</p>
                          )}
                          {item.color && (
                            <p className="item-color">Color: {item.color}</p>
                          )}
                          {item.category && (
                            <p className="item-category">Category: {getCategoryName(item.category, 'tr')}</p>
                          )}
                        </div>
                      ) : (
                        <div className="marketplace-item-details">
                          {(() => {
                            const details = getProductDetails(item);
                            if (details) {
                              return (
                                <>
                                  {details.brand && (
                                    <p className="item-brand">Brand: {details.brand}</p>
                                  )}
                                  {details.color && (
                                    <p className="item-color">Color: {details.color}</p>
                                  )}
                                  {details.dimensions && (
                                    <p className="item-dimensions">Size: {details.dimensions}</p>
                                  )}
                                  {details.base_unit && (
                                    <p className="item-unit">Unit: {details.base_unit}</p>
                                  )}
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="item-actions">
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          disabled={updating === index.toString()}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="quantity-input"
                          value={updating === index.toString() ? '...' : (editingQuantities[index] !== undefined ? editingQuantities[index] : item.quantity)}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string while typing
                            if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                              setEditingQuantities(prev => ({ ...prev, [index]: value }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value > 0) {
                              updateQuantity(index, value);
                            } else {
                              // Reset to current quantity if invalid
                              updateQuantity(index, item.quantity);
                            }
                            // Clear editing state
                            setEditingQuantities(prev => {
                              const newState = { ...prev };
                              delete newState[index];
                              return newState;
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              // Reset to original quantity on escape
                              setEditingQuantities(prev => {
                                const newState = { ...prev };
                                delete newState[index];
                                return newState;
                              });
                              e.currentTarget.blur();
                            }
                          }}
                          disabled={updating === index.toString()}
                          min="1"
                        />
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          disabled={updating === index.toString()}
                        >
                          +
                        </button>
                      </div>

                      <div className="action-links">
                        <button
                          className="action-link delete-link"
                          onClick={() => removeItem(index)}
                          disabled={updating === index.toString()}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="checkout-panel">
            <h2>Request Information</h2>

            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-line">
                <span>Total Products:</span>
                <span>{cartData.items.length} {cartData.items.length === 1 ? 'item' : 'items'}</span>
              </div>
              <div className="summary-line">
                <span>Total Quantity:</span>
                <span>{cartData.items.reduce((total, item) => total + item.quantity, 0)} {cartData.items.reduce((total, item) => total + item.quantity, 0) === 1 ? 'unit' : 'units'}</span>
              </div>
            </div>

            <div className="address-section">
              <h3>Delivery Address *</h3>
              <select
                value={selectedDeliveryAddress}
                onChange={(e) => setSelectedDeliveryAddress(e.target.value)}
                className="address-select"
              >
                <option value="">Select address</option>
                {addresses.map((addr) => (
                  <option key={addr._id} value={addr._id}>
                    {addr.address_name || `${addr.street}, ${addr.city}`}
                    {addr.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="address-section">
              <h3>Invoice Address *</h3>
              <select
                value={selectedInvoiceAddress}
                onChange={(e) => setSelectedInvoiceAddress(e.target.value)}
                className="address-select"
              >
                <option value="">Select address</option>
                {addresses.map((addr) => (
                  <option key={addr._id} value={addr._id}>
                    {addr.address_name || `${addr.street}, ${addr.city}`}
                    {addr.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="contact-section">
              <h3>Contact Information</h3>
              <div className="contact-fields-row">
                <div className="form-group">
                  <label>Contact Person *</label>
                  <input
                    type="text"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="Full Name"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="example@email.com"
                  />
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <button
                className="continue-shopping-btn"
                onClick={() => navigate('/dashboard/user/create-request/marketplace')}
              >
                Add Further Products to Cart
              </button>
              <button
                className="checkout-btn"
                onClick={handleSubmitRequest}
                disabled={submitting || !selectedDeliveryAddress || !selectedInvoiceAddress || !contactPerson || !contactEmail}
              >
                {submitting ? 'Creating...' : 'Complete Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <UserLayout
      user={user}
      currentRoute="/dashboard/user/cart"
      onNavigate={navigate}
      onLogout={() => navigate('/')}
      pageTitle="Cart"
      pageDescription="Check your shopping cart and complete request"
      cartItemCount={cartData?.items?.length || 0}
      disableInnerScroll={true}
      noContentPadding={false}
    >
      {getCartContent()}

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmSubmitRequest}
        title="Complete Request"
        message={`Are you sure you want to create this request with ${cartData?.items?.length || 0} ${cartData?.items?.length === 1 ? 'product' : 'products'}?`}
        confirmText="Create Request"
        cancelText="Cancel"
        type="info"
      />

      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessClose}
        message={successMessage}
        duration={3}
      />
    </UserLayout>
  );
};

export default CartPage;